import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import MetaTags from "react-meta-tags";
import SweetAlert from "react-bootstrap-sweetalert";
import ReactDrawer from "react-drawer";
import { Dropdown } from 'semantic-ui-react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  FormGroup,
  Label,
} from "reactstrap";
import { AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import { motion, AnimatePresence } from "framer-motion";

import { usersApi } from "../../services/UsersService";

import Breadcrumbs from "../../components/Breadcrumb";
import PageLoader from "../../components/Loaders/newPageLoader";
import { showToast } from "../../components/Toast";
import useLoadingProgress from "../../hooks/useLoadingProgress";
import UserProfile from "../../pages/Authentication/UserProfile";
import { MainContent } from "../../components/Table/MainContent";
import { BtnModal } from "../../components/Table/BtnModal";
import { countryOptions } from '../../helpers/country_helper';

export default function UsersList({ profileData, loading }) {
  const {
    data = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = usersApi.useFetchAllUsersQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
  });
  const [addUser] = usersApi.useAddUserMutation();
  const [updateUser] = usersApi.useUpdateUserMutation();
  const [deleteUser] = usersApi.useDeleteUserMutation();

  const [users, setUsersData] = useState([]);
  const [user, setSelectedUser] = useState([]);
  const [profile, setSelectedProfile] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [departments, setDepartmentsData] = useState([]);
  const [permissions, setPermissionsData] = useState([]);
  const [checkedPermissions, setCheckedPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countryError, setCountryError] = useState(false);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [filters, setNoDbFilters] = useState([]);

  const { progress, step, visible } = useLoadingProgress({
    isLoading,
    isFetching,
  });
  useEffect(() => {
    if (data.users) {
      setUsersData(JSON.parse(JSON.stringify(data.users)));
      setDepartmentsData(JSON.parse(JSON.stringify(data.departments)));
      setPermissionsData(JSON.parse(JSON.stringify(data.permissions)));
    }
  }, [data]);
  const [cardHeight, setCardHeight] = useState(0);
  const cardRef = useRef(null);
  useEffect(() => {
    if (!cardRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const newHeight = entry.contentRect.height;
        if (newHeight > 0) setCardHeight(newHeight);
      }
    });
    resizeObserver.observe(cardRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const applyFilters = () => {
      let result = [...users];

      if (filters?.departments?.length > 0) {
        result = result.filter((user) =>
          filters?.departments.includes(user.department._id),
        );
      }

      if (filters?.accountTypes?.length > 0) {
        result = result.filter((user) =>
          filters?.accountTypes.includes(user.accType),
        );
      }

      setFilteredUsers(result);
    };

    applyFilters();
  }, [filters, users]);

  const [position, setPosition] = useState("right");
  const [openDr, setOpenDr] = useState(false);
  /**
   * Handles the event when a user profile is clicked.
   * Sets the selected user profile and opens the right drawer to display user details.
   *
   * @param {Object} user - The user object representing the clicked profile.
   */
  const handleUserClickProfile = (user) => {
    setSelectedProfile(user);
    toggleRightDrawer();
  };
  /**
   * Toggles the visibility of the right drawer.
   * Sets the drawer position to 'right' and switches its open/closed state.
   */
  const toggleRightDrawer = () => {
    setPosition("right");
    setOpenDr((prev) => !prev);
  };
  /**
   * Closes the drawer by setting its open state to false.
   * Typically used as an event handler for closing UI drawers or side panels.
   */
  const onDrawerClose = () => setOpenDr(false);
  // Toggle modal
  const [modalNewUser, setModalNewUser] = useState(false);
  const [modalAccess, setModalAccess] = useState(false);
  /**
   * Toggles the visibility state of the "New User" modal.
   * Uses a callback to invert the current modal state.
   *
   * @function
   * @returns {void}
   */
  const toggleNewUserModal = useCallback(() => {
    setModalNewUser((prev) => !prev);
  }, []);
  /**
   * Toggles the visibility state of the access modal.
   * Uses a callback to invert the current modal state.
   * Intended to be used as an event handler for opening or closing the access modal.
   */
  const toggleAccessModal = useCallback(() => {
    setModalAccess((prev) => !prev);
  }, []);
  /**
   * Handles the event when the "New User" button is clicked.
   * Resets the selected user and access selections, sets edit mode to false,
   * and opens the modal for creating a new user.
   */
  const handleNewUserClick = () => {
    setSelectedUser([]);
    setIsEdit(false);
    toggleNewUserModal();
    setSelectedCountry('');
  };
  /**
   * Handles the event when a user is selected for editing.
   * Sets the selected user's details in the state, resets the selected access,
   * enables edit mode, and opens the user modal for editing.
   *
   * @param {Object} user - The user object containing user details to be edited.
   */
  const handleUserClickEdit = (user) => {
    setSelectedUser(user);
    setIsEdit(true);
    setSelectedCountry(user.country);
    toggleNewUserModal();
  };
  const setCountry = (value) => {
    setSelectedCountry(value)
  };  
  /**
   * Handles the event when a user is selected to view or modify their access.
   * Sets the selected user's information for access management and opens the access modal.
   *
   * @param {Object} user - The user object containing user details.
   * @param {string} user._id - The unique identifier of the user.
   * @param {string} user.fname - The first name of the user.
   * @param {string} user.sname - The surname of the user.
   * @param {any} user.access - The access level or permissions of the user.
   */
  const handleUserClickAccess = (user) => {
    setSelectedUser(user);
    // Initialize checkbox state from user's current permissions
    const initialChecked = {};
    permissions.forEach(type => {
      initialChecked[type.key] = user.permissions?.includes(type.key) || false;
    });
    setCheckedPermissions(initialChecked);
    setOriginalPermissions(initialChecked);
    toggleAccessModal();
  };
  const handlePermissionToggle = (key) => {
    setCheckedPermissions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleModuleToggle = (types) => {
    const allChecked = types.every(t => checkedPermissions[t.key]);
    
    if (allChecked) {
      // Restore original values for this module's permissions
      const restored = types.reduce((acc, t) => ({
        ...acc,
        [t.key]: originalPermissions[t.key] || false,
      }), {});
      setCheckedPermissions(prev => ({ ...prev, ...restored }));
    } else {
      // Select all
      const updates = types.reduce((acc, t) => ({ ...acc, [t.key]: true }), {});
      setCheckedPermissions(prev => ({ ...prev, ...updates }));
    }
  };
  /**
   * Handles the submission of the new user or edit user form.
   * Depending on the mode (add or edit), it constructs the appropriate user object
   * and performs the corresponding API call to add or update a user in the system.
   * Displays toast notifications based on the outcome and toggles the user modal on success.
   *
   * @async
   * @function
   * @param {Event} e - The form submission event.
   * @param {Object} values - The form values containing user details.
   * @returns {Promise<void>}
   */
  const handleNewUserSubmit = async (e, values) => {
    if (!selectedCountry) {
      setCountryError(true);
      return;
    }
    if (isEdit) {
      const updUser = {
        _id: user._id,
        option: "update_user",
        fname: values.fname,
        sname: values.sname,
        workEmail: values.workEmail,
        persEmail: values.persEmail,
        department: values.department,
        supervisor: values.supervisor,
        position: values.position,
        leave_id: user.leave._id,
        access_id: user.access_id, // Pass access_id for updating access
        access_rights: values.access_rights, // Pass new accessRights value
        medLeave: Number(values.medLeave),
        vacLeave: Number(values.vacLeave),
        medLeaveHours: values.medLeave * 8,
        vacLeaveHours: values.vacLeave * 8,
        mobPhoneOne: values.mobPhoneOne,
        mobPhoneTwo: values.mobPhoneTwo,
        deskPhone: values.deskPhone,
        country: values.country,
        accType: values.accType,
        bitrixID: values.bitrixID,
        telegramID: values.telegramID,
        permissions: permissions.filter(p => p.permissionType.includes(values.access_rights)).map(p => p.key)
      };
      try {
        if (checkExistingUser(updUser) === true) {
        } else {
          await updateUser(updUser).unwrap();
          toggleNewUserModal();
          showToast("User is Updated");
        }
      } catch (error) {
        console.error("Failed to Update new user:", error);
        showToast("Failed to Update User information", "error");
      }
    } else {
      const newUser = {
        fname: values["fname"],
        sname: values["sname"],
        workEmail: values["workEmail"],
        persEmail: values["persEmail"],
        password: "test123",
        profilePic: "",
        department: values["department"],
        supervisor: values["supervisor"],
        position: values["position"],
        medLeave: Number(values["medLeave"]),
        vacLeave: Number(values["vacLeave"]),
        medLeaveHours: values["medLeave"] * 8,
        vacLeaveHours: values["vacLeave"] * 8,
        mobPhoneOne: values["mobPhoneOne"],
        mobPhoneTwo: values["mobPhoneTwo"],
        deskPhone: values["deskPhone"],
        country: values["country"],
        accType: values["accType"],
        bitrixID: values["bitrixID"],
        telegramID: values["telegramID"],
        permissions: permissions.filter(p => p.permissionType.includes(values["access_rights"])).map(p => p.key)
      };
      try {
        if (checkExistingUser(newUser) === true) {
        } else {
          await addUser(newUser).unwrap();
          toggleNewUserModal();
          showToast("New User is Added");
        }
      } catch (error) {
        console.error("Failed to add new user:", error);
        showToast("Failed to Add New User", "error");
      }
    }
  };
  /**
   * Checks if a user with the same work email already exists in the users list, excluding the user with the same _id.
   *
   * @param {Object} newUser - The user object to check for existence.
   * @param {string} newUser.workEmail - The work email of the user to check.
   * @param {string|number} newUser._id - The unique identifier of the user to exclude from the check.
   * @returns {boolean} Returns true if another user with the same work email exists, otherwise false.
   */
  const checkExistingUser = (newUser) => {
    return users.some(function (el) {
      return el.workEmail === newUser.workEmail && el._id !== newUser._id;
    });
  };
  /**
   * Handles the submission of updated user access permissions.
   *
   * This function constructs a new user access object based on the provided form values,
   * updates the user access information by calling the `updateUser` function, and then
   * closes the access modal by calling `toggleAccessModal`.
   *
   * @param {Event} e - The form submission event.
   * @param {Object} values - An object containing the updated access permissions for the user.
   */
  const handleValidAccessSubmit = (e, values) => {
    const newUserAccess = {
      _id: user._id,
      permissions: Object.keys(checkedPermissions).filter(key => checkedPermissions[key])
    };
    // save new user
    updateUser(newUserAccess);
    toggleAccessModal();
  }
  const [rowToDelete, setRowToDelete] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  /**
   * Handles the delete action when a user is clicked for deletion.
   * Sets the selected user, marks the user's ID for deletion, and displays the delete confirmation alert.
   *
   * @param {Object} user - The user object representing the user to be deleted.
   */
  const handleUserClickDelete = (user) => {
    setSelectedUser(user);
    setRowToDelete(user._id);
    setShowDeleteAlert(true);
  };
  /**
   * Handles the deletion of a user.
   *
   * This asynchronous function attempts to delete the currently selected user.
   * On successful deletion, it resets relevant UI states and displays a success toast notification.
   * If the deletion fails, it logs the error and shows an error toast notification.
   * The function also ensures that any loading or deleting state can be reset after the operation.
   *
   * @async
   * @function handleDeleteUser
   * @returns {Promise<void>} Resolves when the user deletion process is complete.
   */
  const handleDeleteUser = async () => {
    try {
      // setIsDeleting(true);
      await deleteUser(rowToDelete);
      setRowToDelete(null);
      setShowDeleteAlert(false);
      setSelectedUser([]);
      showToast("User deleted successfully");
    } catch (error) {
      console.error("Failed to delete row:", error);
      showToast("Failed to delete row", "error");
    } finally {
      // setIsDeleting(false); // Reset loading state after completion
    }
  };
  /**
   * Handles the submission of a global action on selected user rows.
   *
   * This function processes the selected rows by updating their access information
   * based on the provided values, then sends asynchronous update requests for each row.
   * Displays a toast notification if any update fails.
   *
   * @async
   * @param {Array<Object>} slctdRows - The array of selected user row objects to update.
   * @param {Object} values - The values containing the action and access state to apply.
   * @param {string} values.action - The action key to update on each row.
   * @param {string} values.access - The access value ("true" or "false") to set for the action.
   * @returns {Promise<void>} Resolves when all updates are complete.
   */
  const handleGlobalActionSubmit = async (slctdRows, values, selectedValues) => {
    const accessValue = values.access === "true";

    const updatedRows = slctdRows.map(row => {
      const currentPermissions = row.permissions || [];
      
      const updatedPermissions = accessValue
        ? [...new Set([...currentPermissions, ...selectedValues])]          // add, no duplicates
        : currentPermissions.filter(p => !selectedValues.includes(p));      // remove

      return {
        _id: row._id,
        permissions: updatedPermissions,
      };
    });

    try {
      // setIsUpdating(true);
      await Promise.all(
        updatedRows.map((row) => {
          const updatedRow = JSON.parse(JSON.stringify(row));
          return updateUser(updatedRow).unwrap();
        }),
        
      );
    } catch (error) {
      showToast("Failed to update row", "error");
    }
  };
  /**
   * Renders the content for the "New User" modal form.
   *
   * This component displays a multi-row form for creating or editing a user, including fields for personal information,
   * department, supervisor, position, contact details, leave days, access rights, and role. It uses AvField components
   * for validation and supports dynamic population of select options for departments and supervisors.
   *
   * @constant
   * @type {JSX.Element}
   * @param {Object} user - The user object containing current form values.
   * @param {Array} departments - Array of department objects for the department select field.
   * @param {Array} users - Array of user objects for the supervisor select field.
   * @param {Object} error - Error object for displaying validation errors.
   * @returns {JSX.Element} The rendered form content for the modal.
   */
  const modalNewUserContent = (
    <>
      <Row>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              name="fname"
              label="First Name"
              type="text"
              errorMessage="Invalid name"
              validate={{
                required: { value: true },
              }}
              value={user.fname || ""}
            />
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              name="sname"
              label="Last Name"
              type="text"
              errorMessage="Invalid surname"
              validate={{
                required: { value: true },
              }}
              value={user.sname || ""}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup
            className={error && error.error ? "text-danger mb-3" : "mb-3"}
          >
            <AvField
              name="workEmail"
              label="Working Email"
              type="email"
              errorMessage="Invalid Email"
              className={error && error.error && "av-invalid is-invalid"}
              validate={{
                required: { value: true },
              }}
              value={user.workEmail || ""}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup className="mb-3">
            <AvField
              name="persEmail"
              label="Additional Email"
              type="email"
              value={user.persEmail || ""}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <FormGroup className="mb-3">
            <AvField
              type="select"
              name="department"
              className="form-select"
              label="Department"
              required
              value={user.length !== 0 && (user.department._id || "")}
            >
              <option>Select Department</option>
              {departments.map((sup) => {
                return (
                  <option key={sup._id} value={sup._id}>
                    {sup.depName}
                  </option>
                );
              })}
            </AvField>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup className="mb-3">
            <AvField
              type="select"
              name="supervisor"
              className="form-select"
              label="Supervisor"
              required
              value={user.supervisor || ""}
            >
              <option>Choose</option>
              <option value="none">None</option>
              {users.map((sup) => {
                return (
                  <option key={sup._id} value={sup.workEmail}>
                    {sup.fname} {sup.sname}
                  </option>
                );
              })}
            </AvField>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup className="mb-3">
            <AvField
              name="position"
              label="Position"
              type="text"
              errorMessage="Invalid Position"
              validate={{
                required: { value: true },
              }}
              value={user.position || ""}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <FormGroup className='mb-3'>
            <Label htmlFor='country_selector'>Country</Label>
            <Dropdown
              placeholder='Select Country'
              fluid
              search
              selection
              options={countryOptions}
              name='country'
              required
              defaultValue={selectedCountry}
              onChange={setCountry}
              id='country_selector'
              error={countryError}
            />
          </FormGroup>
          {/* <FormGroup className="mb-3">
            <AvField
              type="select"
              name="country"
              className="form-select"
              label="Country"
              required
              value={user.country || ""}
            >
              <option>Choose</option>
              <option>Korea</option>
              <option>Malaysia</option>
              <option>Ukraine</option>
            </AvField>
          </FormGroup> */}
        </Col>
        <Col md={4}>
          <FormGroup className="mb-3">
            <AvField
              name="mobPhoneOne"
              label="Mobile Number"
              type="text"
              placeholder="+1-(555)-555-5555"
              errorMessage="Invalid Number"
              validate={{
                required: { value: true },
              }}
              value={user.mobPhoneOne || ""}
            />
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              name="mobPhoneTwo"
              label="Additional Number"
              type="text"
              placeholder="+1-(555)-555-5555"
              value={user.mobPhoneTwo || ""}
            />
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              name="deskPhone"
              label="Office №"
              type="text"
              placeholder="5252"
              errorMessage="Invalid Number"
              value={user.deskPhone || ""}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              name="vacLeave"
              label="No of Vacation days"
              type="number"
              errorMessage="Invalid Number"
              validate={{
                required: { value: true },
              }}
              value={user.length !== 0 && (user.leave.vacLeave || 12)}
            />
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              name="medLeave"
              label="No of Medical days"
              type="number"
              errorMessage="Invalid Number"
              validate={{
                required: { value: true },
              }}
              value={user.length !== 0 && (user.leave.medLeave || 12)}
            />
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              type="select"
              name="access_rights"
              className="form-select"
              label="Access Rights"
              required
              value={user.access_rights || ""}
            >
              <option value="keep_current_access">Keep Current Access</option>
              <option value="default_access">Default Access</option>
              {[...new Set(permissions.flatMap(t => t.permissionType))].map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </AvField>
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              type="select"
              name="accType"
              className="form-select"
              label="Role"
              required
              value={user.accType || ""}
            >
              <option>Choose</option>
              <option value="Employee">Employee</option>
              <option value="Admin">Admin</option>
              <option value="Outsider">Outsider</option>
              <option value="laid_off">Laid Off</option>
            </AvField>
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              name="bitrixID"
              label="Bitrix ID"
              type="number"
              errorMessage="Invalid Number"
              validate={{
                required: { value: true },
              }}
              value={user.bitrixID || ""}
            />
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              name="telegramID"
              label="Telegram ID"
              type="text"
              errorMessage="Invalid Number"
              validate={{
                required: { value: true },
              }}
              value={user.telegramID || ""}
            />
          </FormGroup>
        </Col>
      </Row>
    </>
  );
  const modalAccessContent = user.length !== 0 && (() => {
    // Group permissions by module
    const grouped = permissions.reduce((acc, type) => {
      const mod = type.module || "Other";
      if (!acc[mod]) acc[mod] = [];
      acc[mod].push(type);
      return acc;
    }, {});

    // Sort groups largest → smallest
    const sortedGroups = Object.entries(grouped).sort((a, b) => b[1].length - a[1].length);

    // Greedy bin-pack into 4 columns
    const NUM_COLS = 4;
    const columns = Array.from({ length: NUM_COLS }, () => ({ groups: [], total: 0 }));

    sortedGroups.forEach(([moduleName, types]) => {
      const minColIdx = columns.reduce((minIdx, col, i) =>
        col.total < columns[minIdx].total ? i : minIdx, 0);
      columns[minColIdx].groups.push([moduleName, types]);
      columns[minColIdx].total += types.length;
    });

    const renderGroup = (moduleName, types) => {
      const allChecked = types.every(t => checkedPermissions[t.key]);
      return (
        <div key={moduleName} className="mb-4">
          <FormGroup className="module-header d-flex align-items-center justify-content-between form-check form-switch form-switch-lg ps-2 py-2 mb-1">
            <Label for={moduleName} className="mb-0 fw-bold" style={{ fontSize: "1.2rem" }}>
              {moduleName}
            </Label>
            {types.length > 1 && (
              <AvField name={moduleName} id={moduleName} type="checkbox" value={moduleName} onChange={() => handleModuleToggle(types)} checked={allChecked}/>
            )}
          </FormGroup>
          <ul className="tree">
            {types.map((type) => (
              <li key={type.key} className="tree-child">
                <FormGroup className="ms-3 d-flex justify-content-between form-check form-switch form-switch-lg">
                  <Label for={type.key}>{type.label}</Label>
                  <AvField
                    name={type.key}
                    id={type.key}
                    type="checkbox"
                    checked={checkedPermissions[type.key] || false}
                    onChange={() => handlePermissionToggle(type.key)}
                    value={user.permissions?.includes(type.key) || ""}
                  />
                </FormGroup>
              </li>
            ))}
          </ul>
        </div>
      );
    };

    return (
      <Row>
        {columns.map((col, i) => (
          <Col key={i} md={3}>
            {col.groups.map(([moduleName, types]) => renderGroup(moduleName, types))}
          </Col>
        ))}
      </Row>
    );
  })();
  /**
   * Renders a radio group component for selecting user access permissions.
   *
   * This component provides two radio options: "Grant Access" and "Revoke Access".
   * It uses the AvRadioGroup and AvRadio components to allow users to choose whether
   * to grant or revoke access. The selection is required for form validation.
   *
   * @constant
   * @type {JSX.Element}
   */
  const globalActionExtraContent = (
    <AvRadioGroup
      name="access"
      className="access_radio ms-3 d-flex"
      validate={{ required: { value: true } }}
    >
      <AvRadio className="" label="Grant Acess" value="true" />
      <AvRadio className="" label="Revoke Access" value="false" />
    </AvRadioGroup>
  );
  const columns = [
    {
      text: "No",
      dataField: "",
      sort: true,
      editable: false,
      csvExport: false,
      formatter: (cell, row, rowIndex) => rowIndex + 1,
      headerClasses: "id-header",
      footer: "",
    },
    {
      dataField: "profilePic",
      text: "#",
      formatter: (cellContent, user) => (
        <>
          {!user.profilePic ? (
            <div className="avatar-md">
              <span className="avatar-title rounded-circle">
                {user.fname.charAt(0)}
              </span>
            </div>
          ) : (
            <div>
              <img
                className="rounded-circle avatar-md"
                src={user.profilePic}
                alt=""
              />
            </div>
          )}
        </>
      ),
    },
    {
      text: "KoruMS ID",
      dataField: "_id",
      sort: true,
      formatter: (cellContent, user) => (
        <>
          <h5 className="font-size-16 mb-1">{user._id}</h5>
        </>
      ),
    },
    {
      text: "Full Name",
      dataField: "fname",
      sort: true,
      formatter: (cellContent, user) => (
        <>
          <h5 className="font-size-16 mb-1">
            <Link
              to="#"
              className="text-dark fname"
              onClick={() => handleUserClickProfile(user)}
              disabled={open}
            >
              {user.fname} {user.sname}
            </Link>
          </h5>
          <p className="text-muted mb-0">{user.position}</p>
        </>
      ),
    },
    {
      text: "Email",
      dataField: "workEmail",
      sort: true,
      formatter: (cellContent, user) => (
        <>
          <h5 className="font-size-16 mb-1">{user.workEmail}</h5>
          <p className="text-muted mb-0">{user.persEmail}</p>
        </>
      ),
    },
    {
      text: "Phone #",
      dataField: "mobPhoneOne",
      sort: true,
      formatter: (cellContent, user) => (
        <>
          <h5 className="font-size-16 mb-1">{user.mobPhoneOne}</h5>
          <p className="text-muted mb-0">{user.mobPhoneTwo}</p>
          <p className="text-muted mb-0">{user.deskPhone}</p>
        </>
      ),
    },
    {
      text: "Department",
      dataField: "department.depName",
      sort: true,
      formatter: (cellContent, user) => (
        <>
          <h5 className="font-size-16 mb-1">
            {user.department && user.department.depName}
          </h5>
          {/* <p className='text-muted mb-0'>
            {user.supervisor ? (
              <span>
                Supervisor:&nbsp;
                {user.users_supervisor.map((supName, index) => {
                  return (
                    <span key={index}>
                      {supName.fname}
                      &nbsp;
                      {supName.sname}
                    </span>
                  );
                })}
              </span>
            ) : null}
          </p> */}
        </>
      ),
    },
    {
      text: `${JSON.parse(localStorage.getItem("authUser")).result.accType === "Admin" ? "Leaves" : ""}`,
      dataField: "vacLeave",
      sort: true,
      formatter: (cellContent, user) => (
        <>
          {JSON.parse(localStorage.getItem("authUser")).result.accType ===
            "Admin" && (
            <>
              <p className="text-muted mb-0">
                Vacation Leaves: {user?.leave?.vacLeave}
              </p>
              <p className="text-muted mb-0">
                Medical Leaves: {user?.leave?.medLeave}
              </p>
            </>
          )}
        </>
      ),
    },
    {
      // text: `${props.profile.accType === "Admin" ? "Account Type" : ""}`,
      text: "Account Type",
      dataField: "accType",
      sort: true,
      formatter: (cellContent, user) => (
        <>
          {JSON.parse(localStorage.getItem("authUser")).result.accType ===
            "Admin" && <>{user.accType}</>}
        </>
      ),
    },
    {
      dataField: "menu",
      isDummyField: true,
      editable: false,
      text: `${JSON.parse(localStorage.getItem("authUser")).result.accType === "Admin" ? "Actions" : ""}`,
      formatter: (cellContent, user) => (
        <>
          {JSON.parse(localStorage.getItem("authUser")).result.accType ===
            "Admin" && (
            <div className="d-flex gap-3">
              <>
                <Link className="text-primary" to="#">
                  <i
                    className="mdi mdi-lock font-size-18"
                    id="edittooltip"
                    onClick={() => handleUserClickAccess(user)}
                  ></i>
                </Link>
                <Link className="text-success" to="#">
                  <i
                    className="mdi mdi-pencil font-size-18"
                    id="edittooltip"
                    onClick={() => handleUserClickEdit(user)}
                  ></i>
                </Link>
                <Link className="text-danger" to="#">
                  <i
                    className="mdi mdi-delete font-size-18"
                    id="deletetooltip"
                    onClick={() => handleUserClickDelete(user)}
                  ></i>
                </Link>
              </>
            </div>
          )}
        </>
      ),
    },
  ];
  const defaultSorted = [
    {
      dataField: "fname", // if dataField is not match to any column you defined, it will be ignored.
      order: "asc", // desc or asc
    },
  ];
  const depsOptions = departments
    .sort((a, b) => a.depName.localeCompare(b.depName))
    .map(({ _id, depName }) => ({
      key: _id,
      label: depName,
      icon: "",
      colorClass: "bg-primary",
    }));
  const accTypeOptions = [...new Set(users.map((u) => u.accType))].map(
    (type) => ({ key: type, label: type, icon: "", colorClass: "bg-primary" }),
  );
  const accessGroups = permissions.map(({ key, label }) => ({
    value: key,
    text: label,
  }));
  const accessGroupsMulti = permissions.map(({ key, label }) => ({
    value: key,
    label: label,
  }));
  const [selectedValues, setSelectedValues] = useState([]);
  const filterFormValues = { accountTypes: ["Employee", "Outsider", "Admin"] };
  return (
    <React.Fragment>
      <PageLoader progress={progress} step={step} visible={visible} />
      <div className="page-content">
        <MetaTags>
          <title>Employees List</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title="Admin" breadcrumbItem="Employees List" />
          <Row className="card-section">
            <Col lg="12">
              <div className="pcss3t logtb pcss3t-effect-scale pcss3t-theme-1">
                <ul>
                  <AnimatePresence key="1" exitBeforeEnter>
                    <motion.li
                      className={`tab-content tab-content-1 typography`}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.3 }}
                      style={{ display: "block" }}
                    >
                      <div id="card-section" ref={cardRef}>
                        <Row className="card-section">
                          <Col lg="12">
                            <Card>
                              <CardBody className="cardBodySticky">
                                <ToolkitProvider
                                  keyField="_id"
                                  columns={columns}
                                  data={
                                    filteredUsers.length > 0
                                      ? filteredUsers
                                      : users
                                  }
                                  search
                                >
                                  {(props) => (
                                    <React.Fragment>
                                      <MainContent
                                        page={{
                                          pg: "users",
                                          props: props,
                                          cardHeight,
                                          showSearch: true,
                                          showCSV: true,
                                          showInTblFltr: true,
                                          showDbFilter: false,
                                          showBtn: true,
                                          columns: props.baseProps.columns,
                                          data: props.baseProps.data,
                                          defaultSorted,
                                        }}
                                        // filter options
                                        filterOptions={{
                                          departments: depsOptions,
                                          accountTypes: accTypeOptions,
                                        }}
                                        filterDbOptions={{ filterFormValues }}
                                        setNoDbFilters={setNoDbFilters}
                                        // options for adding/updating modal and button
                                        btnCrtModalOptions={{
                                          btnLabelCrt: "INVITE NEW EMPLOYEE",
                                          modalSize: "xl",
                                          modalTitle: isEdit
                                            ? "Edit Employee"
                                            : "Add Employee",
                                          modalBtnLabel: isEdit
                                            ? "Edit Employee"
                                            : "Add Employee",
                                          modalAdd: modalNewUser,
                                          toggleAddModal: toggleNewUserModal,
                                          handleNewClick: handleNewUserClick,
                                          modalContent: modalNewUserContent,
                                          handleSubmit: handleNewUserSubmit,
                                        }}
                                        // table data opotions
                                        tableOptions={{
                                          includeSelect: true,
                                          manualColumnWidthsIndex: [1],
                                          manualColumnWidths: [90],
                                        }}
                                        // table actions
                                        tableActions={{
                                          globalActionOptions: accessGroups,
                                          globalActionBtnLbl: "APPLY",
                                          multiselect: true,
                                          globalActionOptionsMulti: accessGroupsMulti,
                                          selectedValues,
                                          setSelectedValues,
                                          globalActionExtraContent,
                                          handleGlobalActionSubmit,
                                        }}
                                      />
                                      <BtnModal
                                        modalSize="xl"
                                        modalSizeExtra="modal-xxl"
                                        modalTitle={`Access for ${user.fname} ${user.sname}`}
                                        modalBtnLabel="Edit Access"
                                        modalOpen={modalAccess}
                                        toggleModal={toggleAccessModal}
                                        handleSubmit={handleValidAccessSubmit}
                                      >
                                        {modalAccessContent}
                                      </BtnModal>
                                      <SweetAlert
                                        show={showDeleteAlert}
                                        danger
                                        showCancel
                                        confirmBtnText="Yes, delete it!"
                                        confirmBtnBsStyle="danger"
                                        title="Are you sure?"
                                        onConfirm={handleDeleteUser}
                                        onCancel={() =>
                                          setShowDeleteAlert(false)
                                        }
                                        focusCancelBtn
                                      >
                                        <p className="fw-bold">
                                          {user.fname} {user.sname}
                                        </p>{" "}
                                        will be deleted from the system!
                                      </SweetAlert>
                                      <ReactDrawer
                                        open={openDr}
                                        className="drawer-open"
                                        position={position}
                                        onClose={onDrawerClose}
                                      >
                                        <UserProfile
                                          selectedProfile={profile}
                                          profileData={profileData}
                                        />
                                      </ReactDrawer>
                                    </React.Fragment>
                                  )}
                                </ToolkitProvider>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                    </motion.li>
                  </AnimatePresence>
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}